import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, ChevronDown, ChevronRight, LayoutDashboard, Users, MapPin, Home, ClipboardCheck,
  Bug, Crown, Target, Wheat, BarChart3, DollarSign, Settings, MessageSquare, HelpCircle,
  Shield, Bell, Calendar, Printer, BookOpen, Scale, Package, Scissors, ShoppingBag,
  FileText, Mic, Image, AtSign, Check, Globe, Scan, CreditCard, PlayCircle, RotateCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import TourEngine from '../components/TourEngine';

const Section = ({ title, icon: Icon, children, defaultOpen = false, color = 'amber' }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const colors = {
    amber: 'bg-amber-50 hover:bg-amber-100 border-amber-200',
    purple: 'bg-purple-50 hover:bg-purple-100 border-purple-200',
    teal: 'bg-teal-50 hover:bg-teal-100 border-teal-200',
    blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
    green: 'bg-green-50 hover:bg-green-100 border-green-200',
    red: 'bg-red-50 hover:bg-red-100 border-red-200',
  };
  const iconColors = {
    amber: 'text-amber-600', purple: 'text-purple-600', teal: 'text-teal-600',
    blue: 'text-blue-600', green: 'text-green-600', red: 'text-red-600',
  };
  return (
    <div className={`border rounded-lg mb-3 overflow-hidden ${colors[color]?.split(' ')[2] || 'border-stone-200'}`} data-testid={`help-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <button onClick={() => setIsOpen(!isOpen)} className={`w-full flex items-center gap-3 p-4 ${colors[color]} transition-colors text-left`}>
        {Icon && <Icon className={`w-5 h-5 ${iconColors[color]}`} />}
        <span className="font-semibold text-stone-800 flex-1">{title}</span>
        {isOpen ? <ChevronDown className="w-5 h-5 text-stone-500" /> : <ChevronRight className="w-5 h-5 text-stone-500" />}
      </button>
      {isOpen && <div className="p-4 bg-white border-t border-stone-100">{children}</div>}
    </div>
  );
};

const Step = ({ num, children }) => (
  <div className="flex gap-3 mb-2">
    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">{num}</span>
    <p className="text-sm text-stone-700 pt-0.5">{children}</p>
  </div>
);

const Tip = ({ children }) => (
  <div className="bg-blue-50 border-l-4 border-blue-400 p-3 my-3 text-sm text-stone-700">
    <strong className="text-blue-700">Tip:</strong> {children}
  </div>
);

const Note = ({ children }) => (
  <div className="bg-amber-50 border-l-4 border-amber-400 p-3 my-3 text-sm text-stone-700">
    <strong className="text-amber-700">Note:</strong> {children}
  </div>
);

const Badge = ({ color = 'amber', children }) => {
  const styles = { amber: 'bg-amber-100 text-amber-700', green: 'bg-green-100 text-green-700', purple: 'bg-purple-100 text-purple-700', blue: 'bg-blue-100 text-blue-700' };
  return <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${styles[color]}`}>{children}</span>;
};

const HELP_SECTIONS = [
  // ===== GETTING STARTED =====
  {
    id: 'getting-started', title: 'Getting Started', icon: HelpCircle, color: 'green',
    roles: ['admin', 'owner', 'crew'],
    keywords: ['start', 'begin', 'setup', 'first', 'account', 'login'],
    content: () => (
      <>
        <h4 className="font-semibold mb-2">Welcome to Thrive!</h4>
        <p className="text-sm text-stone-600 mb-3">Thrive is a comprehensive beekeeping management system for tracking hives, inspections, treatments, crew, and more.</p>
        <Step num={1}>Sign up or log in at the Login page. Admin accounts have full access.</Step>
        <Step num={2}>Set up your first Apiary (yard) from the Dashboard or Apiaries page.</Step>
        <Step num={3}>Add hives to your yard — assign hive numbers, types, and locations.</Step>
        <Step num={4}>Start recording inspections, tracking mite counts, and managing your crew.</Step>
        <Tip>Use the sidebar navigation to jump between sections. Items with a lock icon require an upgraded plan.</Tip>
      </>
    ),
  },

  // ===== DASHBOARD =====
  {
    id: 'dashboard', title: 'Dashboard', icon: LayoutDashboard, color: 'amber',
    roles: ['admin', 'owner'],
    keywords: ['dashboard', 'overview', 'home', 'summary', 'calendar', 'weather', 'notifications', 'overdue'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Your central command center showing all key metrics at a glance.</p>
        <h5 className="font-medium text-sm mb-1">Key Features:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>Stats Cards:</strong> Total hives, yards, inspections due, pending tasks</li>
          <li><strong>This Week:</strong> Upcoming tasks, overdue inspections, and reminders with apiary filter</li>
          <li><strong>Monthly Calendar:</strong> Visual overview of scheduled tasks and inspections</li>
          <li><strong>Weather Widget:</strong> Auto-fetches from apiary GPS (falls back to browser location)</li>
          <li><strong>Notifications:</strong> Task completions, crew updates, and system alerts</li>
          <li><strong>Weekly Crew Report:</strong> Automated summary of crew productivity</li>
        </ul>
        <Tip>Overdue inspections respect your seasonal schedule — hives in {"winter pause"} yards will not show as overdue.</Tip>
      </>
    ),
  },

  // ===== APIARIES / YARDS =====
  {
    id: 'apiaries', title: 'Apiaries / Yards', icon: MapPin, color: 'green',
    roles: ['admin', 'owner'],
    keywords: ['apiary', 'yard', 'location', 'gps', 'season', 'winter', 'add yard'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Manage your beekeeping locations. Each yard can hold multiple hives.</p>
        <h5 className="font-medium text-sm mb-1">Creating a Yard:</h5>
        <Step num={1}>Go to Apiaries tab and click &quot;Add Yard&quot;</Step>
        <Step num={2}>Enter name, location description, and GPS coordinates (click map or use auto-detect)</Step>
        <Step num={3}>GPS enables weather auto-fill and hemisphere detection for seasonal schedules</Step>

        <h5 className="font-medium text-sm mt-3 mb-1">Seasonal Inspection Schedule:</h5>
        <p className="text-sm text-stone-600 mb-2">Each yard can have per-season inspection intervals:</p>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-2">
          <li><strong>Spring:</strong> 10-day default interval</li>
          <li><strong>Summer:</strong> 14-day default interval</li>
          <li><strong>Autumn:</strong> 21-day default interval</li>
          <li><strong>Winter:</strong> Paused by default (no overdue alerts)</li>
        </ul>
        <Note>Hemisphere is auto-detected from GPS latitude. Southern hemisphere seasons are inverted.</Note>
      </>
    ),
  },

  // ===== HIVES =====
  {
    id: 'hives', title: 'Hive Management', icon: Home, color: 'amber',
    roles: ['admin', 'owner'],
    keywords: ['hive', 'add hive', 'bulk', 'qr code', 'hive detail', 'queen', 'equipment', 'stack'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Track every hive in your operation with detailed records.</p>
        <h5 className="font-medium text-sm mb-1">Hive Features:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>Hive Cards:</strong> Show status, yard, last inspection, brood/super counts</li>
          <li><strong>Hive Detail:</strong> Queen info, weight trend, mite count chart, inspection history grouped by month</li>
          <li><strong>QR Codes:</strong> Print bulk QR codes (4×5 grid per page) for field scanning</li>
          <li><strong>Bulk Add:</strong> Add multiple hives at once to a yard</li>
          <li><strong>Bulk Split:</strong> 4-step wizard for splitting multiple hives simultaneously</li>
          <li><strong>Has Supers Filter:</strong> Quick filter to show only hives with active supers</li>
        </ul>
        <h5 className="font-medium text-sm mt-3 mb-1">Hive Status Types:</h5>
        <div className="flex flex-wrap gap-2 mb-2">
          <Badge color="green">Active</Badge>
          <Badge color="blue">Ready (empty equipment)</Badge>
          <Badge color="amber">Queenless</Badge>
          <Badge>Inactive</Badge>
        </div>
        <Tip>Ready hives are excluded from overdue inspection calculations — they are empty equipment waiting for swarms or splits.</Tip>
      </>
    ),
  },

  // ===== INSPECTIONS =====
  {
    id: 'inspections', title: 'Inspections', icon: ClipboardCheck, color: 'blue',
    roles: ['admin', 'owner', 'crew'],
    keywords: ['inspection', 'inspect', 'frame', 'brood', 'super', 'photo', 'video', 'report', 'pdf'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Record detailed inspection data for each hive.</p>
        <h5 className="font-medium text-sm mb-1">Inspection Form Sections:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>General:</strong> Date, inspector, weather (auto-filled from apiary GPS)</li>
          <li><strong>Queen Status:</strong> Queen seen, age, performance, laying pattern</li>
          <li><strong>Frame-by-Frame:</strong> Toggle brood, honey, nectar, pollen per frame per box</li>
          <li><strong>Brood Management:</strong> Add/remove brood boxes with quantity tracking</li>
          <li><strong>Super Management:</strong> Add/remove supers (type-aware for Langstroth, Flow, etc.)</li>
          <li><strong>Health:</strong> Disease signs, pest observations, temperament</li>
          <li><strong>Actions Taken:</strong> Admin-configurable action items (Split Hive, Combine Hives, etc.)</li>
          <li><strong>Photos/Videos:</strong> Upload photos inline (plan limits apply)</li>
        </ul>
        <h5 className="font-medium text-sm mt-3 mb-1">Mite Monitoring (within Inspection):</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>AI Frame Scanner:</strong> Upload a photo for AI-powered varroa detection</li>
          <li><strong>Record Mite Count:</strong> Log alcohol wash or sugar roll counts</li>
          <li><strong>Start Treatment:</strong> Launch the treatment wizard — select the recommended treatment and schedule</li>
          <li><strong>View Plans:</strong> Opens a dialog (stays on inspection) showing active treatments, recent mite counts, and the last 3 treatment history to help rotate treatments</li>
        </ul>
        <h5 className="font-medium text-sm mt-3 mb-1">Harvest Pipeline:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li>Removing supers or capped honey frames during an inspection automatically creates a <strong>Pending Harvest</strong></li>
          <li>Go to Record Harvests to see "Supers Ready for Harvest" and record the yield</li>
        </ul>
        <h5 className="font-medium text-sm mt-3 mb-1">Splits & Combines:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>Split Hive:</strong> Record split details including removed frames, destination (new or existing hive)</li>
          <li><strong>Combined Hives:</strong> Record which hives were combined and how</li>
          <li>Split information shows on the hive card with the exact <strong>date/timestamp</strong></li>
        </ul>
        <h5 className="font-medium text-sm mt-3 mb-1">Viewing Reports:</h5>
        <p className="text-sm text-stone-600 mb-2">Each inspection generates a detailed report with PDF export option. Reports show all data including frame diagrams, photos, and action items.</p>
        <Note>PDF downloads may be blocked in the preview environment. They work correctly in production.</Note>
      </>
    ),
  },

  // ===== CREW MANAGEMENT =====
  {
    id: 'crew', title: 'Crew Management', icon: Users, color: 'purple',
    roles: ['admin', 'owner'],
    keywords: ['crew', 'team', 'member', 'invite', 'task', 'assign', 'check-in', 'gps', 'productivity', 'weekly report'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Manage your beekeeping team with task assignment, GPS check-ins, and productivity tracking.</p>
        <h5 className="font-medium text-sm mb-1">Features:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>Invite Members:</strong> Add crew with email + password. They log in at the Crew Login page.</li>
          <li><strong>Task Assignment:</strong> Create tasks with title, description, priority, due date, and hive/yard assignment</li>
          <li><strong>Multi-assign:</strong> Assign tasks to multiple crew members simultaneously</li>
          <li><strong>Task Grouping:</strong> Group active tasks by Crew or by Yard</li>
          <li><strong>Bulk Actions:</strong> Select multiple tasks and Start/Complete them all at once</li>
          <li><strong>GPS Check-in:</strong> Crew members check in/out at yards with GPS tracking</li>
          <li><strong>Productivity Metrics:</strong> Time on site, visits per yard, crew rankings</li>
          <li><strong>Weekly Report:</strong> Automated Monday email summary + dashboard card</li>
          <li><strong>Print PDF:</strong> Generate printable task list with yard map</li>
        </ul>
        <Tip>Task filters (priority, due date) combine with grouping for powerful task views.</Tip>
      </>
    ),
  },

  // ===== TEAM CHAT =====
  {
    id: 'team-chat', title: 'Team Chat', icon: MessageSquare, color: 'amber',
    roles: ['admin', 'owner', 'crew'],
    keywords: ['chat', 'message', 'communication', 'channel', 'voice', 'mention', 'photo', 'announcement'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Real-time team communication with channels, @mentions, photos, and voice notes.</p>
        <h5 className="font-medium text-sm mb-1">Channels:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>#General:</strong> Open discussion for the whole team</li>
          <li><strong>#Announcements:</strong> Admin-only posting for important updates</li>
          <li><strong>Yard Channels:</strong> Auto-created for each apiary — discuss yard-specific topics</li>
        </ul>
        <h5 className="font-medium text-sm mt-3 mb-1">Features:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>@Mentions:</strong> Type @ then a name to mention team members (they get notified)</li>
          <li><strong>Photo Sharing:</strong> Click the image icon to share photos in chat</li>
          <li><strong>Voice Notes:</strong> Click the microphone icon to record and send voice messages</li>
          <li><strong>Read Receipts:</strong> Single check = delivered, double blue check = read by others</li>
          <li><strong>Unread Badges:</strong> Channel unread counts + total count in sidebar</li>
          <li><strong>Push Notifications:</strong> Click &quot;Enable Notifications&quot; in the chat header to receive browser push alerts when new messages arrive, even when the tab is closed</li>
        </ul>

        <h5 className="font-medium text-sm mt-3 mb-1">Task Comments:</h5>
        <p className="text-sm text-stone-600 mb-2">Every task also has a built-in comment thread. Click &quot;Comments&quot; on any task card to expand the discussion. Supports the same features: text, photos, voice notes, and @mentions.</p>
        <Tip>Crew members access Team Chat from their dashboard via the {'"Team Chat"'} button.</Tip>
      </>
    ),
  },

  // ===== MITE CONTROL =====
  {
    id: 'mite-control', title: 'Mite Control & Treatments', icon: Bug, color: 'red',
    roles: ['admin', 'owner'],
    keywords: ['mite', 'treatment', 'varroa', 'oxalic', 'formic', 'apivar', 'rotation', 'resistance', 'guide'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Comprehensive mite management with 17 treatment types, rotation planning, and resistance warnings.</p>
        <h5 className="font-medium text-sm mb-1">Treatment Wizard:</h5>
        <Step num={1}>Select hives to treat</Step>
        <Step num={2}>Choose treatment type (with {"Recommended for You"} banner based on rotation analysis)</Step>
        <Step num={3}>Set schedule and dosage</Step>
        <Step num={4}>Review and create treatment plan</Step>

        <h5 className="font-medium text-sm mt-3 mb-1">Key Features:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>How-To Guides:</strong> Step-by-step instructions for every treatment type</li>
          <li><strong>Resistance Warnings:</strong> Red/orange banners for high/moderate resistance treatments (Apistan, CheckMite+, Apivar)</li>
          <li><strong>Rotation Planner:</strong> 0-100 score showing rotation health, penalizes consecutive same-chemical use</li>
          <li><strong>Efficacy Report:</strong> Compare treatment effectiveness across all hives</li>
          <li><strong>Seasonal Recommendations:</strong> AI suggests best treatment for current season</li>
          <li><strong>Email Reminders:</strong> Get notified when it is time for seasonal treatments</li>
        </ul>
        <Note>Norroa japonica treatment is included for beekeepers dealing with Norroa mites.</Note>
      </>
    ),
  },

  // ===== QUEEN REARING =====
  {
    id: 'queen-rearing', title: 'Queen Rearing', icon: Crown, color: 'purple',
    roles: ['admin', 'owner'],
    keywords: ['queen', 'rearing', 'breed', 'nuc', 'mating', 'batch', 'move', 'queenless'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Track breeder queens, mating NUCs, queen cells, and batches.</p>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>Breeder Queens:</strong> Record lineage, performance, age</li>
          <li><strong>Mating NUCs:</strong> Track NUC status, queen cell placement</li>
          <li><strong>Queen Batches:</strong> Group queen cells into batches with grafting dates</li>
          <li><strong>Move to Hive:</strong> Smart dialog highlights queenless, ready, and recently-split hives. Automatically creates an inspection record.</li>
        </ul>
        <Tip>Queenless status is derived from inspection data where queen_seen=false.</Tip>
      </>
    ),
  },

  // ===== SWARM TRAPS =====
  {
    id: 'swarm-traps', title: 'Swarm Traps', icon: Target, color: 'green',
    roles: ['admin', 'owner'],
    keywords: ['swarm', 'trap', 'catch', 'rescue', 'lure', 'performance', 'material'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Track swarm traps, catches, and rescue operations.</p>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>Trap Management:</strong> Track location, material, lure type, volume, height</li>
          <li><strong>Performance Dashboard:</strong> Catches timeline, success rates, best configuration card</li>
          <li><strong>Compare Traps:</strong> Performance by material, lure type, and volume</li>
          <li><strong>Rescue Swarms:</strong> Log rescued swarms with location, cluster details, and outcome</li>
          <li><strong>PDF Export:</strong> Full report with rescue data and performance comparisons</li>
        </ul>
      </>
    ),
  },

  // ===== HARVESTS =====
  {
    id: 'harvests', title: 'Harvests & Sales', icon: Wheat, color: 'amber',
    roles: ['admin', 'owner'],
    keywords: ['harvest', 'honey', 'super', 'extract', 'yield', 'sale', 'product'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Track harvests, products, and sales.</p>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1">
          <li><strong>Supers Ready:</strong> Removed supers auto-appear in the harvest queue</li>
          <li><strong>Record Harvests:</strong> Log yield per hive with date and notes</li>
          <li><strong>Products:</strong> Create products (jars, frames, comb) with pricing</li>
          <li><strong>Sales:</strong> Record sales with customer info and revenue tracking</li>
          <li><strong>Harvest Settings:</strong> Configure jar sizes, extraction methods</li>
        </ul>
      </>
    ),
  },

  // ===== ANALYTICS =====
  {
    id: 'analytics', title: 'Analytics', icon: BarChart3, color: 'blue',
    roles: ['admin', 'owner'],
    keywords: ['analytics', 'report', 'chart', 'graph', 'trend', 'ai', 'summary'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">AI-powered analytics and reporting.</p>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1">
          <li><strong>AI Summary:</strong> GPT-generated overview of your operation&apos;s health</li>
          <li><strong>Hive Trends:</strong> Weight, mite count, and inspection frequency charts</li>
          <li><strong>Yard Comparison:</strong> Compare performance across apiaries</li>
          <li><strong>Treatment Efficacy:</strong> Which treatments work best for your hives</li>
        </ul>
      </>
    ),
  },

  // ===== HIVE COSTS =====
  {
    id: 'hive-costs', title: 'Hive Costs', icon: DollarSign, color: 'amber',
    roles: ['admin', 'owner'],
    keywords: ['cost', 'price', 'equipment', 'budget', 'expense', 'feeder', 'tool'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Track per-hive component costs and yard-level equipment costs.</p>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1">
          <li><strong>Per-Hive Components:</strong> Brood boxes, supers, frames, queen excluders, etc.</li>
          <li><strong>Yard Equipment:</strong> Tools, heavy equipment shared across the yard</li>
          <li><strong>Cost Summary:</strong> Total investment per hive and per yard</li>
        </ul>
        <Note>Per-hive and yard-level costs are separated to avoid double-counting.</Note>
      </>
    ),
  },

  // ===== CREW DASHBOARD =====
  {
    id: 'crew-dashboard', title: 'Crew Dashboard (Crew Members)', icon: Users, color: 'teal',
    roles: ['crew'],
    keywords: ['crew', 'dashboard', 'task', 'check-in', 'field', 'inspection', 'knowledge', 'ai assistant'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">The crew member&apos;s home base for field work.</p>
        <h5 className="font-medium text-sm mb-1">Features:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>My Tasks:</strong> View assigned tasks with priority, due date, and hive/yard info</li>
          <li><strong>Task Comments:</strong> Communicate with the team on each task — text, photos, voice notes, @mentions</li>
          <li><strong>GPS Check-in:</strong> Tap {"Check In"} on a yard card to log your arrival with GPS</li>
          <li><strong>Record Inspections:</strong> Open the full inspection form directly from a task</li>
          <li><strong>Knowledge Library:</strong> Access training resources and AI assistant</li>
          <li><strong>Team Chat:</strong> Jump into the team chat via the {"Team Chat"} button</li>
          <li><strong>Notifications:</strong> Bell icon shows new task assignments and mentions</li>
          <li><strong>Calendar:</strong> Monthly view of your upcoming tasks</li>
        </ul>
        <Tip>The app works offline! Completed tasks are queued and synced when you are back online.</Tip>
      </>
    ),
  },

  // ===== BUG REPORTS =====
  {
    id: 'bug-reports', title: 'Bug Reporting', icon: Bug, color: 'red',
    roles: ['admin', 'owner', 'crew'],
    keywords: ['bug', 'report', 'issue', 'error', 'problem', 'ladybug'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Report issues directly from the app using the ladybug icon.</p>
        <h5 className="font-medium text-sm mb-1">For All Users:</h5>
        <Step num={1}>Click the ladybug icon (bottom-right of any page)</Step>
        <Step num={2}>Select bug type, priority, and describe the issue</Step>
        <Step num={3}>Optionally attach a screenshot and add steps to reproduce</Step>
        <Step num={4}>Submit — the admin will be notified and AI auto-categorizes the report</Step>

        <h5 className="font-medium text-sm mt-3 mb-1">For Admins:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1">
          <li><strong>Bug Reports Page:</strong> Filter by status (New, In Progress, Resolved, etc.)</li>
          <li><strong>AI Categorization:</strong> Auto-assigns feature area and generates dev summary</li>
          <li><strong>Copy for Dev:</strong> One-click markdown copy of all report details</li>
          <li><strong>Reply to User:</strong> Send email response directly from the admin panel</li>
        </ul>
      </>
    ),
  },

  // ===== ADMIN CONFIGURATION =====
  {
    id: 'admin-config', title: 'Admin Configuration', icon: Settings, color: 'purple',
    roles: ['admin'],
    keywords: ['admin', 'config', 'setting', 'plan', 'subscription', 'user', 'feature'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Admin-only settings and management tools.</p>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>Subscription Plans:</strong> Create/edit plans with feature comparison table + customer preview</li>
          <li><strong>User Management:</strong> View all users, manage roles and status</li>
          <li><strong>Configuration:</strong> Customize inspection actions (Brood, Super, Equipment, Maintenance, Health, Feeding)</li>
          <li><strong>App Settings:</strong> Company name, logo, notification email, terms and conditions</li>
          <li><strong>Marketplace Admin:</strong> Manage marketplace listings</li>
        </ul>
      </>
    ),
  },

  // ===== BULK OPERATIONS =====
  {
    id: 'bulk-ops', title: 'Bulk Operations', icon: Package, color: 'blue',
    roles: ['admin', 'owner'],
    keywords: ['bulk', 'split', 'add', 'multiple', 'batch'],
    content: () => (
      <>
        <h5 className="font-medium text-sm mb-1">Bulk Add Hives:</h5>
        <p className="text-sm text-stone-600 mb-3">Add multiple hives to a yard at once with auto-numbering.</p>

        <h5 className="font-medium text-sm mb-1">Bulk Split:</h5>
        <Step num={1}><strong>Event Details:</strong> Name the split event and set default method</Step>
        <Step num={2}><strong>Select Hives:</strong> Choose parent hives (filter by yard)</Step>
        <Step num={3}><strong>Review & Adjust:</strong> Customize method per hive, set queen status for daughters</Step>
        <Step num={4}><strong>Complete:</strong> Daughter hives created with auto-numbering (e.g., 104a, 104b)</Step>
        <Tip>Each daughter hive gets an automatic first inspection record documenting the split.</Tip>
      </>
    ),
  },

  // ===== COMMUNITY =====
  {
    id: 'community', title: 'Community & Knowledge', icon: Globe, color: 'teal',
    roles: ['admin', 'owner'],
    keywords: ['community', 'forum', 'hive mind', 'knowledge', 'marketplace', 'beekeeper', 'find'],
    content: () => (
      <>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1">
          <li><strong>Hive Mind:</strong> Forum with channels (Ask, Tips, Harvesting, Pest Control). Location-aware with {"Near Me"} filter.</li>
          <li><strong>Knowledge Library:</strong> Articles, glossary, resources, and AI assistant</li>
          <li><strong>Marketplace:</strong> Buy/sell equipment and products</li>
          <li><strong>Find Beekeepers:</strong> Map-based search for nearby beekeepers</li>
        </ul>
      </>
    ),
  },

  // ===== OFFLINE & PWA =====
  {
    id: 'offline', title: 'Offline & PWA Support', icon: Globe, color: 'blue',
    roles: ['admin', 'owner', 'crew'],
    keywords: ['offline', 'pwa', 'install', 'mobile', 'field', 'no internet'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Thrive works offline for field use in remote areas.</p>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>Install as App:</strong> Click {"Install"} when prompted to add Thrive to your home screen</li>
          <li><strong>Offline Mode:</strong> View hives, yards, and tasks without internet</li>
          <li><strong>Queue & Sync:</strong> Actions performed offline are queued and synced when reconnected</li>
          <li><strong>Offline Banner:</strong> Shows pending action count when offline</li>
        </ul>
        <Note>Photos and voice notes require internet for upload. Text actions are queued offline.</Note>
      </>
    ),
  },
  {
    id: 'find-honey', title: 'Find Local Honey (Public Marketplace)', icon: Globe, color: 'green',
    roles: ['admin', 'owner'],
    keywords: ['honey', 'marketplace', 'public', 'find', 'beekeeper', 'map', 'products', 'reviews', 'listing'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">A public-facing directory where buyers can find local beekeepers and their products.</p>
        <h5 className="font-medium text-sm mb-1">For Beekeepers (Sellers):</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>Enable Public Listing:</strong> Go to My Profile → toggle on "List me on Find Local Honey"</li>
          <li><strong>Add Products:</strong> Go to Products → Add Product with name, price, description, stock quantity, and photo</li>
          <li><strong>Product Types:</strong> Free plan users can only list Honey products. Upgrade to list Nucs, Queens, Equipment, etc.</li>
          <li><strong>Logo:</strong> Upload your business logo in My Profile — it appears next to your name on the directory</li>
          <li><strong>Location:</strong> Uncheck "Hide my Location" so buyers can find you on the map</li>
        </ul>
        <h5 className="font-medium text-sm mb-1">For Buyers:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>Search:</strong> Search by location, product type (Queen, Nuc, Honey), or beekeeper name</li>
          <li><strong>Filter by Type:</strong> Use filter chips (All, Queen, Nuc, Honey, etc.) to narrow results</li>
          <li><strong>Map View:</strong> Toggle between List and Map view to find nearby beekeepers</li>
          <li><strong>Reviews:</strong> Read reviews from other buyers, or leave your own star rating and comment</li>
          <li><strong>Contact:</strong> View beekeeper bio, phone, email, and product details</li>
        </ul>
        <Note>Reviews require admin approval before they appear publicly. Admins can manage reviews at Admin → Reviews.</Note>
      </>
    ),
  },
  {
    id: 'subscription-plans', title: 'Subscription Plans & Feature Access', icon: CreditCard, color: 'amber',
    roles: ['admin', 'owner'],
    keywords: ['plan', 'subscription', 'upgrade', 'free', 'hobby', 'lock', 'feature', 'pricing'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Thrive uses a tiered subscription model. Features are unlocked based on your plan.</p>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>Free / Backyard Beekeeper:</strong> 2 hives, basic inspections, mite control, calendar, analytics, honey-only products</li>
          <li><strong>Hobby Beekeeper:</strong> More hives, knowledge library, marketplace, AI reports, voice notes, DIY ideas</li>
          <li><strong>Growing Apiary:</strong> Splits, swarms, harvests, swarm traps, team chat</li>
          <li><strong>Association / Sideline Pro:</strong> Queen management, crew management, all features</li>
        </ul>
        <h5 className="font-medium text-sm mb-1">How it works:</h5>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li>Locked features show a 🔒 icon in the sidebar — click to see upgrade options</li>
          <li>Visit <strong>Plans</strong> in the sidebar to see your current plan and compare tiers</li>
          <li>Your current plan is highlighted with a "YOUR CURRENT PLAN" banner</li>
          <li>Upgrade anytime — features unlock instantly</li>
        </ul>
        <Tip>Analytics and Compare Hives are available on ALL plans, including Free.</Tip>
      </>
    ),
  },
  {
    id: 'version-updates', title: 'App Version & Updates', icon: Settings, color: 'blue',
    roles: ['admin', 'owner', 'crew'],
    keywords: ['version', 'update', 'refresh', 'new version', 'deploy'],
    content: () => (
      <>
        <p className="text-sm text-stone-600 mb-3">Thrive shows your current version at the bottom of the sidebar.</p>
        <ul className="list-disc pl-5 text-sm text-stone-600 space-y-1 mb-3">
          <li><strong>Version Badge:</strong> Shows version number and environment (preview/production) at the bottom of the sidebar</li>
          <li><strong>New Version Alert:</strong> When an update is deployed, an amber banner appears: "A new version is available!" with a Refresh Now button</li>
          <li><strong>Dismiss:</strong> Click × to dismiss the banner, or "Refresh Now" to load the latest version</li>
        </ul>
        <Note>If pages seem outdated, check the version badge and click "Refresh Now" if prompted. You can also hard-refresh your browser (Ctrl+Shift+R).</Note>
      </>
    ),
  },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReplayToursSection = () => {
  const [tours, setTours] = useState([]);
  const [activeTour, setActiveTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/tours`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setTours(data.tours || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const resetTour = async (tourId) => {
    try {
      await fetch(`${API_URL}/api/tours/${tourId}/progress`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      // Refresh tours
      const res = await fetch(`${API_URL}/api/tours`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setTours(data.tours || []);
    } catch { }
  };

  if (loading || tours.length === 0) return null;

  return (
    <div className="mb-6" data-testid="replay-tours-section">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h3 className="text-lg font-bold text-amber-900 flex items-center gap-2 mb-3">
          <PlayCircle className="h-5 w-5 text-amber-600" /> Guided Tours
        </h3>
        <p className="text-sm text-amber-700 mb-4">Replay any tour to refresh your knowledge of a feature.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {tours.map(tour => {
            const completed = tour.progress?.completed;
            return (
              <div key={tour.id} className="flex items-center justify-between bg-white rounded-lg border border-amber-100 px-3 py-2.5">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {completed ? <Check className="h-4 w-4 text-green-500 flex-shrink-0" /> : <PlayCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />}
                  <div className="truncate">
                    <span className="text-sm font-medium text-stone-800">{tour.name}</span>
                    {tour.page_view && <span className="text-[10px] text-stone-400 ml-1.5">({tour.page_view})</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {completed && (
                    <button onClick={() => resetTour(tour.id)} className="p-1 text-stone-400 hover:text-amber-600 rounded" title="Reset & Replay">
                      <RotateCcw className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <Button size="sm" onClick={() => {
                    if (tour.page_view) {
                      navigate(`/?view=${tour.page_view}&startTour=${tour.slug}`);
                    } else {
                      setActiveTour(tour);
                    }
                  }} className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid={`replay-${tour.slug}`}>
                    {completed ? 'Replay' : 'Start'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {activeTour && (
        <TourEngine tour={activeTour} onComplete={() => setActiveTour(null)} onSkip={() => setActiveTour(null)} />
      )}
    </div>
  );
};

export default function UserManual() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeRole, setActiveRole] = useState('all');

  const filteredSections = useMemo(() => {
    return HELP_SECTIONS.filter(s => {
      const roleMatch = activeRole === 'all' || s.roles.includes(activeRole);
      const searchMatch = !search ||
        s.title.toLowerCase().includes(search.toLowerCase()) ||
        s.keywords.some(k => k.toLowerCase().includes(search.toLowerCase()));
      return roleMatch && searchMatch;
    });
  }, [search, activeRole]);

  const roles = [
    { id: 'all', label: 'All Guides', color: 'bg-stone-100 text-stone-700' },
    { id: 'admin', label: 'Admin', color: 'bg-purple-100 text-purple-700' },
    { id: 'owner', label: 'Owner', color: 'bg-amber-100 text-amber-700' },
    { id: 'crew', label: 'Crew', color: 'bg-teal-100 text-teal-700' },
  ];

  return (
    <div className="bg-stone-50" data-testid="help-center">
      {/* Header */}
      <div className="p-4">
        <div className="max-w-7xl">
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-amber-600" />
            Help Center
          </h1>
          <p className="text-sm text-stone-500 mt-1">Everything you need to know about Thrive</p>
        </div>
      </div>

      <div className="max-w-7xl px-4 py-6">
        {/* Search + Role Filter */}
        <div className="mb-6 space-y-3">
          <div className="flex items-center gap-2 bg-white rounded-xl px-4 py-3 border border-stone-200 shadow-sm">
            <Search className="w-5 h-5 text-stone-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search help topics... (e.g., 'mite', 'crew', 'offline')"
              className="flex-1 bg-transparent outline-none text-stone-800 placeholder:text-stone-400"
              data-testid="help-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-stone-400 hover:text-stone-600">
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {roles.map(r => (
              <button
                key={r.id}
                onClick={() => setActiveRole(r.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  activeRole === r.id ? r.color + ' ring-2 ring-offset-1 ring-amber-400' : 'bg-white text-stone-500 border border-stone-200 hover:bg-stone-50'
                }`}
                data-testid={`role-filter-${r.id}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Guided Tours — Replay Section (at top) */}
        <ReplayToursSection />

        {/* Results count */}
        {search && (
          <p className="text-sm text-stone-500 mb-3">
            {filteredSections.length} result{filteredSections.length !== 1 ? 's' : ''} for &quot;{search}&quot;
          </p>
        )}

        {/* Sections */}
        {filteredSections.map(section => (
          <Section
            key={section.id}
            title={section.title}
            icon={section.icon}
            color={section.color}
            defaultOpen={!!search}
          >
            {section.content()}
          </Section>
        ))}

        {filteredSections.length === 0 && (
          <div className="text-center py-12 text-stone-400">
            <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No help topics match your search.</p>
            <button onClick={() => { setSearch(''); setActiveRole('all'); }} className="text-amber-600 underline mt-2 text-sm">
              Clear filters
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 p-4 bg-white rounded-xl border border-stone-200 text-center">
          <p className="text-sm text-stone-500">
            Cannot find what you are looking for? Use the <strong>ladybug icon</strong> to report issues or request help.
          </p>
        </div>
      </div>
    </div>
  );
}
